import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  CalendarRange, Trophy, IdCard, Activity, ListOrdered, Flag, FileSpreadsheet, MessagesSquare, PhoneOff, ArrowRight, Check,
} from 'lucide-react';

const condensed = { fontFamily: 'Barlow Condensed' };

const PROBLEMS = [
  { icon: FileSpreadsheet, before: 'Spreadsheets and paper draws', after: 'Schedules, standings and brackets that update themselves' },
  { icon: MessagesSquare, before: 'Chasing scores in group chats', after: 'Live scores and stats entered right from the sideline' },
  { icon: PhoneOff, before: 'Phone tag with referees', after: 'Officials assigned, confirmed by email and tracked for pay' },
];

const PILLARS = [
  { icon: CalendarRange, title: 'Scheduling', desc: 'Round robin, pools and brackets in seconds, with venues, courts, game days and blackout dates.' },
  { icon: Trophy, title: 'Competition Management', desc: 'Teams, rosters, scores, playoffs and co-organizers, all in one place.' },
  { icon: IdCard, title: 'Player Information', desc: 'Rosters with photos and public player profile pages your athletes can share.' },
  { icon: Activity, title: 'Statistics', desc: 'Live stat tracking, AI box-score scanning and season leaderboards.' },
  { icon: ListOrdered, title: 'Standings & Playoffs', desc: 'Standings with tiebreakers update with every score, and brackets advance automatically.' },
  { icon: Flag, title: 'Officials', desc: 'Assign referees and umpires, collect confirmations and track officials pay.' },
];

const Eyebrow = ({ children }) => (
  <span className="text-sm tracking-[0.2em] uppercase font-bold text-[#CF142B]" style={condensed}>{children}</span>
);

export const ProblemSection = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24" data-testid="problem-section">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <Eyebrow>The Problem We Solve</Eyebrow>
        <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-[#00247D] mt-3 leading-[0.95]" style={condensed}>
          Less Admin.<br /><span className="text-[#CF142B]">More Game Time.</span>
        </h2>
        <p className="text-slate-600 mt-5 text-lg leading-relaxed">
          Running a competition means hours of admin: building schedules, collecting scores, updating standings and lining up officials.
          Schedule Guru takes that work off your plate.
        </p>
      </div>
      <div className="lg:col-span-7 flex flex-col gap-4">
        {PROBLEMS.map((p, i) => (
          <div key={p.before} className="flex gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#00247D]/30 hover:shadow-card-hover transition-[border-color,box-shadow] duration-300 animate-fade-in-up"
            style={{ animationDelay: `${i * 90}ms` }} data-testid={`problem-item-${i}`}>
            <div className="w-11 h-11 shrink-0 rounded-xl bg-[#00247D]/10 flex items-center justify-center"><p.icon className="w-5 h-5 text-[#00247D]" /></div>
            <div className="min-w-0">
              <div className="text-sm text-slate-500 line-through decoration-[#CF142B]/60">{p.before}</div>
              <div className="text-base font-semibold text-slate-900 mt-0.5 flex items-start gap-2">
                <Check className="w-4 h-4 text-[#009E49] mt-1 shrink-0" />{p.after}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const PillarsSection = () => (
  <section id="features" className="bg-slate-50 border-y border-slate-200 scroll-mt-20" data-testid="pillars-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
      <div className="max-w-3xl mb-12">
        <Eyebrow>Why Schedule Guru</Eyebrow>
        <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-[#00247D] mt-3 leading-[0.95]" style={condensed}>
          Your Whole Competition.<br />One Platform.
        </h2>
        <p className="text-slate-600 mt-4 text-lg">Scheduling, competition management, player information, statistics, standings and officials work together, so nothing gets entered twice.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {PILLARS.map((p, i) => (
          <div key={p.title} className="group p-6 rounded-2xl bg-white border border-slate-200 hover:-translate-y-1 hover:border-[#00247D]/30 hover:shadow-card-hover transition-[transform,border-color,box-shadow] duration-300"
            data-testid={`pillar-${i}`}>
            <div className="w-11 h-11 rounded-xl bg-[#00247D] flex items-center justify-center mb-4 group-hover:bg-[#CF142B] transition-colors duration-300">
              <p.icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold uppercase tracking-wide text-[#00247D]" style={condensed}>{p.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FreeSchedulerBand = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="free-scheduler-band">
      <div className="relative overflow-hidden rounded-3xl bg-hero-gradient px-6 py-12 sm:px-12 lg:px-16 lg:py-16">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center px-3 py-1 rounded-md bg-amber-400/15 border border-amber-300/40 text-amber-200 text-xs font-bold tracking-[0.2em] uppercase" style={condensed}>
              Free Round Robin Schedule Generator
            </span>
            <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-white mt-4 leading-[0.95]" style={condensed}>
              Start With a Schedule.<br /><span className="text-amber-300">Manage Your Entire League.</span>
            </h2>
            <p className="text-slate-300 mt-4 text-lg max-w-2xl">
              Generate your schedule in seconds, then discover everything Schedule Guru offers to simplify competition management.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-3">
            <Button size="lg" onClick={() => navigate('/create')} data-testid="free-scheduler-btn"
              className="bg-white text-[#00247D] hover:bg-slate-100 btn-transition h-14 px-8 text-base font-bold rounded-md">
              Create a Free Schedule <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <span className="text-sm text-slate-300">No account needed for round robin.</span>
          </div>
        </div>
      </div>
    </section>
  );
};
